import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, X, FileImage, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Vehicle } from "@/hooks/useVehicles";
import analytics from "@/lib/segment";

interface UploadReceiptsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExpensesParsed: (expenses: ParsedReceiptExpense[], receiptUrls: string[]) => void;
  vehicles: Vehicle[];
  expenseTypes: string[];
  paidByOptions: string[];
}

export interface ParsedReceiptExpense {
  expense_date: string;
  expense_name: string;
  amount: number;
  expense_type: string;
  vehicle_id?: string | null;
  paid_by?: string | null;
  paid_to?: string | null;
  notes?: string | null;
  mileage?: number | null;
  _receipt_url?: string;
  _parse_failed?: boolean;
}

async function callParseReceipts(
  _token: string,
  body: object,
): Promise<{ expenses?: ParsedReceiptExpense[]; error?: string }> {
  const { data, error } = await supabase.functions.invoke("parse-receipts", { body });
  if (error) return { error: error.message };
  return data;
}

export const UploadReceiptsDialog = ({
  isOpen,
  onClose,
  onExpensesParsed,
  vehicles,
  expenseTypes,
  paidByOptions,
}: UploadReceiptsDialogProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const validFiles = selected.filter((f) => {
      if (!f.type.startsWith("image/") && f.type !== "application/pdf") {
        toast.error(`${f.name} is not a supported file type`);
        return false;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    setFiles((prev) => [...prev, ...validFiles].slice(0, 50));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProcess = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);
    setProgress(5);
    setCurrentFileIndex(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get session token for auth
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No session");

      // Upload all files to storage
      const uploadedUrls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        setStatusText(`Uploading receipt ${i + 1} of ${files.length}...`);
        setCurrentFileIndex(i + 1);
        const file = files[i];
        const fileExt = file.name.split(".").pop();
        const fileName = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("expense-receipts")
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("expense-receipts")
          .getPublicUrl(fileName);

        uploadedUrls.push(publicUrl);
        setProgress(5 + ((i + 1) / files.length) * 25);
      }

      // Call parse-receipts per file for real-time progress
      const allParsedExpenses: ParsedReceiptExpense[] = [];
      const vehicleData = vehicles.map((v) => ({
        id: v.id,
        name: v.name,
        year: v.year,
        make: v.make,
        model: v.model,
      }));

      let errorCount = 0;
      for (let i = 0; i < uploadedUrls.length; i++) {
        setStatusText(`Analyzing receipt ${i + 1} of ${uploadedUrls.length} with Captain AI...`);
        setCurrentFileIndex(i + 1);
        setProgress(30 + ((i) / uploadedUrls.length) * 60);

        try {
          const result = await callParseReceipts(session.access_token, {
            imageUrls: [uploadedUrls[i]],
            vehicles: vehicleData,
            expenseTypes,
            paidByOptions,
          });

          if (result.error || !result.expenses || result.expenses.length === 0) {
            console.error(`Error parsing receipt ${i + 1}:`, result.error || "No expenses returned");
            errorCount++;
            // Create a placeholder expense so the file is still attached
            allParsedExpenses.push({
              expense_date: new Date().toISOString().split("T")[0],
              expense_name: `Receipt ${i + 1} (needs review)`,
              amount: 0,
              expense_type: "Other",
              _receipt_url: uploadedUrls[i],
              _parse_failed: true,
            });
          } else {
            const tagged = result.expenses.map((exp: ParsedReceiptExpense) => ({
              ...exp,
              _receipt_url: uploadedUrls[i],
              _parse_failed: false,
            }));
            allParsedExpenses.push(...tagged);
          }
        } catch (parseErr: any) {
          console.error(`Exception parsing receipt ${i + 1}:`, parseErr);
          errorCount++;
          allParsedExpenses.push({
            expense_date: new Date().toISOString().split("T")[0],
            expense_name: `Receipt ${i + 1} (needs review)`,
            amount: 0,
            expense_type: "Other",
            _receipt_url: uploadedUrls[i],
            _parse_failed: true,
          });
        }

        setProgress(30 + ((i + 1) / uploadedUrls.length) * 60);
      }

      setProgress(95);
      setStatusText("Creating expense entries...");

      setProgress(100);
      analytics.track("Receipts Imported", {
        receipt_count: files.length,
        expense_count: allParsedExpenses.length,
        error_count: errorCount,
      });
      onExpensesParsed(allParsedExpenses, uploadedUrls);
      if (errorCount > 0) {
        toast.warning(`Extracted ${allParsedExpenses.length} expense(s) from ${files.length} receipt(s). ${errorCount} receipt(s) need manual review.`);
      } else {
        toast.success(`Extracted ${allParsedExpenses.length} expense(s) from ${files.length} receipt(s)`);
      }
      handleClose(true);
    } catch (error: any) {
      console.error("Receipt processing error:", error);
      toast.error("Failed to process receipts: " + (error.message || "Unknown error"));
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setStatusText("");
      setCurrentFileIndex(0);
    }
  };

  const handleClose = (force = false) => {
    if (!isProcessing || force) {
      setFiles([]);
      setProgress(0);
      setStatusText("");
      setCurrentFileIndex(0);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Receipts</DialogTitle>
          <DialogDescription>
            Upload scanned receipts or photos. Captain AI will automatically extract expense details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone */}
          <div
            className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => !isProcessing && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm font-medium">Click to select files</p>
            <p className="text-xs text-muted-foreground mt-1">
              Images or PDFs, up to 50 files, 10MB each
            </p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 rounded-md bg-muted/50"
                >
                  <FileImage className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm truncate flex-1">{file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(0)}KB
                  </span>
                  {!isProcessing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Progress */}
          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                {statusText}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose()} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleProcess}
            disabled={files.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Analyze {files.length} Receipt{files.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
